"use client";

import * as React from "react";

import { AdSlot } from "./ad-slot";
import { useAdRuntime } from "./ad-provider";

/**
 * A native (fluid) slot rendered by a GAM Native style — the "特約文章" list row
 * or grid card. Sits exactly where the editorial sponsored item sits today; the
 * editorial markup is the fallback, so pages look unchanged until a native
 * creative is trafficked.
 *
 *   <NativeAdSlot unit="InBanner">{sponsoredRow}</NativeAdSlot>   list row (auto-numbered)
 *   <NativeAdSlot unit="NativeAd1">{sponsoredCard}</NativeAdSlot>  grid card
 */
export function NativeAdSlot({
  unit = "InBanner",
  className = "",
  keepEmpty = false,
  requestOverflow = false,
  showOverflow = false,
  frameCss,
  children,
}: {
  unit?: string;
  className?: string;
  /** No editorial fallback, and an unfilled slot is not copied from a sibling. */
  keepEmpty?: boolean;
  /** Past the unit count, still request GAM. An empty answer shows `children`. */
  requestOverflow?: boolean;
  /**
   * A repeated unit stays on screen so GPT can answer it. An empty answer
   * copies a creative that already painted, instead of falling back to the
   * article while the slot is `display: none`.
   */
  showOverflow?: boolean;
  /** CSS inserted into a friendly creative frame (hover, type). Cross-origin frames ignore it. */
  frameCss?: string;
  children?: React.ReactNode;
}) {
  const rt = useAdRuntime();
  const wrapRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!frameCss) return;
    const root = wrapRef.current;
    if (!root) return;
    const apply = () => {
      root.querySelectorAll("iframe").forEach((frame) => {
        try {
          const doc = frame.contentDocument;
          if (!doc?.head || doc.getElementById("hkej-frame-css")) return;
          const style = doc.createElement("style");
          style.id = "hkej-frame-css";
          style.textContent = frameCss;
          doc.head.appendChild(style);
        } catch {
          /* cross-origin */
        }
      });
    };
    apply();
    const mo = new MutationObserver(apply);
    mo.observe(root, { childList: true, subtree: true });
    const timer = window.setInterval(apply, 400);
    const stop = window.setTimeout(() => window.clearInterval(timer), 8000);
    return () => {
      mo.disconnect();
      window.clearInterval(timer);
      window.clearTimeout(stop);
    };
  }, [frameCss]);
  return (
    <div ref={wrapRef} style={{ display: "contents" }}>
      {keepEmpty || requestOverflow || showOverflow ? <style>{OVERFLOW_SLOT_CSS}</style> : null}
      <AdSlot
        unit={unit}
        className={className}
        keepEmpty={keepEmpty}
        requestOverflow={requestOverflow}
        showOverflow={showOverflow}
        style={rt.enabled ? { width: "100%" } : undefined}
      >
        {children}
      </AdSlot>
    </div>
  );
}

/**
 * The running dev server does not always rebuild globals.css. These rules
 * keep a repeated native in the document until GAM answers, so GPT defines
 * the second div. An empty answer still collapses a keep-empty host, and a
 * slot with an article shows that article.
 */
const OVERFLOW_SLOT_CSS = `
.hkej-ad.hkej-ad[data-ad-format="NativeAd"][data-ad-request-overflow][data-ad-overflow][data-ad-state="pending"],
.hkej-ad.hkej-ad[data-ad-format="InBanner"][data-ad-request-overflow][data-ad-overflow][data-ad-state="pending"],
.hkej-ad.hkej-ad[data-ad-format="NativeAd"][data-ad-keep-empty][data-ad-overflow][data-ad-state="pending"],
.hkej-ad.hkej-ad[data-ad-format="InBanner"][data-ad-keep-empty][data-ad-overflow][data-ad-state="pending"] {
  display: flex;
}
.hkej-ad-wrap:has(.hkej-ad[data-ad-format="NativeAd"][data-ad-request-overflow][data-ad-overflow][data-ad-state="pending"]) > .hkej-ad-fallback,
.hkej-ad-wrap:has(.hkej-ad[data-ad-format="InBanner"][data-ad-request-overflow][data-ad-overflow][data-ad-state="pending"]) > .hkej-ad-fallback {
  display: none;
}
`;
